import { createContext, useContext, useSyncExternalStore } from 'react';
import type { WidgetStore } from './stores/widgetStore';
import type { TutorialStore } from './stores/tutorialStore';
import type { SupportWidgetState, TutorialStoreState } from './types';

/* ---- Widget store context ---------------------------------------- */

export const WidgetStoreContext = createContext<WidgetStore | null>(null);

export function useWidgetStore(): SupportWidgetState;
export function useWidgetStore<T>(selector: (s: SupportWidgetState) => T): T;
export function useWidgetStore<T>(
  selector?: (s: SupportWidgetState) => T,
): T | SupportWidgetState {
  const store = useContext(WidgetStoreContext);
  if (!store) {
    throw new Error(
      '[@netrun/support-widget] useWidgetStore must be used inside <SupportWidget>',
    );
  }
  const sel = selector ?? ((s: SupportWidgetState) => s as unknown as T);
  return useSyncExternalStore(
    store.subscribe,
    () => sel(store.getState()),
    () => sel(store.getState()),
  );
}

/* ---- Tutorial store context -------------------------------------- */

export const TutorialStoreContext = createContext<TutorialStore | null>(null);

export function useTutorialStore(): TutorialStoreState;
export function useTutorialStore<T>(
  selector: (s: TutorialStoreState) => T,
): T;
export function useTutorialStore<T>(
  selector?: (s: TutorialStoreState) => T,
): T | TutorialStoreState {
  const store = useContext(TutorialStoreContext);
  if (!store) {
    throw new Error(
      '[@netrun/support-widget] useTutorialStore must be used inside <SupportWidget>',
    );
  }
  const sel = selector ?? ((s: TutorialStoreState) => s as unknown as T);
  return useSyncExternalStore(
    store.subscribe,
    () => sel(store.getState()),
    () => sel(store.getState()),
  );
}
